import { HelpService } from 'src/app/services/help.service';
import { Component, ViewChild } from '@angular/core';
import { asyncScheduler, filter, map, Observable, observeOn, switchMap, takeUntil } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { ArchiveJobManager } from '../../../managers/archive-job.manager';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ArchiveJob, ArchiveJobStatus } from '../../../models/archive-job.model';
import { OkCancelDialogComponent } from '../../dialogs/ok-cancel/ok-cancel-dialog.component';
import { AddArchiveDialogComponent } from './add-archive-dialog/add-archive-dialog.component';
import { RunArchiveJobRequest } from '../../../models/ui/job-request/run-archive-job-request.model';
import { ProcessingManager } from '../../../managers/processing.manager';
import { DownloadArchiveDialogComponent } from './download-archive-dialog/download-archive-dialog.component';
import { DownloadArchiveJobRequest } from '../../../models/ui/job-request/download-archive-job-request.model';
import { Collection } from '../../../models/ui/collection.model';
import { BaseComponent } from '../../base.component';
import { Store } from "@ngrx/store";
import { selectIsProcessing } from "@store/global/global.selectors";

@Component({
    selector: 'lex-archive-jobs',
    templateUrl: './archive-jobs.component.html',
    styleUrls: ['./archive-jobs.component.scss'],
    standalone: false
})
export class ArchiveJobsComponent extends BaseComponent {
    public ArchiveJobStatus = ArchiveJobStatus;

    public totalEntries$: Observable<number>;
    public hasPreviousPage$: Observable<boolean>;
    public hasNextPage$: Observable<boolean>;
    public dataSource$: Observable<MatTableDataSource<ArchiveJob>>;

    private skip: number = 0;
    private take: number = 200;

    public isProcessing: boolean = true;

    @ViewChild('paginator', { static: false }) paginator: MatPaginator;

    public columnsToDisplay = [
        'archiveJobId',
        'title',
        'status',
        'createdBy',
        'runBy',
        'dateTimeCreated',
        'dateTimeRun',
        'totalDocCount',
        'archiveBaseNumberStart',
        'archiveBaseNumberDigits',
        'docSizeArchived',
        'IsCreateTiff',
        'edit',
        'delete'
    ];

    constructor(
        private archiveJobManager: ArchiveJobManager,
        private processingManager: ProcessingManager,
        private matDialog: MatDialog,
        private helpService: HelpService,
		private store: Store) {
        super();
        this.helpService.updateHelpLinkUrl('https://sites.google.com/a/ediscoveryplatform.com/help/home/technical-notes-available/archive-entire-cases');
        this.store.select(selectIsProcessing)
            .pipe(takeUntil(this.destroy$))
            .subscribe(isProcessing => this.isProcessing = isProcessing);

        this.dataSource$ = this.archiveJobManager.archiveJobCollection$.pipe(
            filter((collection: Collection<ArchiveJob>) => !!collection),
            map((archiveJobCollection: Collection<ArchiveJob>) => new MatTableDataSource(archiveJobCollection?.items))
        );

        this.totalEntries$ = this.archiveJobManager.archiveJobCollection$.pipe(
            map((collection: Collection<ArchiveJob>) => collection?.totalItems || 0)
        );

        this.hasNextPage$ = this.totalEntries$.pipe(
            observeOn(asyncScheduler),
            map(() => this.paginator?.hasNextPage())
        );

        this.hasPreviousPage$ = this.totalEntries$.pipe(
            observeOn(asyncScheduler),
            map(() => this.paginator?.hasPreviousPage())
        );
        this.refreshArchiveJobs();
    }

    public refreshArchiveJobs(): void {
        this.archiveJobManager.getArchiveJobs({
            skip: this.skip,
            take: this.take
        });
    }

    public loadPreviousPage(): void {
        this.paginator?.previousPage();
    }

    public loadNextPage(): void {
        this.paginator?.nextPage();
    }

    public movePage(event): void {
        this.skip = event.pageIndex * event.pageSize;
        this.take = event.pageSize;
        this.refreshArchiveJobs();
    }

    public delete(id: string): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = true;
        dialogConfig.data = {
            title: `Delete Archive Job`,
            message: `Delete selected archive job?
				<br /> Only the selected archive job will be deleted and not the original case documents.
				<br /> This action cannot be undone.`
        };

        this.matDialog.open(OkCancelDialogComponent, dialogConfig).afterClosed().pipe(
            switchMap((okClicked: boolean) => {
                if (okClicked)
                    return this.archiveJobManager.delete(id);
            })
        ).subscribe(() => this.refreshArchiveJobs());
    }

    public addArchiveJob() {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = true;
        this.matDialog.open(AddArchiveDialogComponent, dialogConfig).afterClosed().subscribe((next: any) => {
            if (typeof next === 'string' && next.startsWith('Error: ')) {
                console.error(next);
            }
            this.refreshArchiveJobs();
        });
    }

    public runJob(element: ArchiveJob) {
        const request = new RunArchiveJobRequest();
        request.stepParams = { archiveJobId: element.archiveJobId };
        this.processingManager.scheduleStep({ stepRequest: request }).subscribe(() => this.refreshArchiveJobs());
    }

    public downloadJob(element: ArchiveJob) {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = true;
        dialogConfig.width = '550px';
        this.matDialog.open(DownloadArchiveDialogComponent, dialogConfig).afterClosed().subscribe((downloadPath: any) => {
            const lowerPath = downloadPath?.toLowerCase();
            if (!lowerPath || !lowerPath.startsWith('\\\\amazon\\') && !lowerPath.startsWith('\\\\austin\\'))
                return;

            const request = new DownloadArchiveJobRequest();
            request.stepParams = {
                archiveJobId: element.archiveJobId,
                downloadPath: downloadPath
            };
            this.processingManager.scheduleStep({ stepRequest: request }).subscribe(() => this.refreshArchiveJobs());
        });
    }
}
